import { useCallback, useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Route, Routes, Navigate, useLocation, useNavigate, useSearchParams } from "react-router-dom"
import { RootState } from "../../store/store"
import {
  resetSlice,
  setQrParams,
  stopPaymentStatusPolling,
} from "../../store/slices/c2bSlice"
import { parseQrPaymentUrl } from "../../utils/qrcode"
import { OperationStatus } from "../../api/interface"
import { ROUTES_PATHS } from "../../utils/constants"
import C2BPushScan from "../../pages/C2BPush/Scan"
import C2BPushPayment from "../../pages/C2BPush/Payment"
import C2BPushSuccess from "../../pages/C2BPush/Success"
import C2BPushError from "../../pages/C2BPush/Error"
import { useCreateC2BPayment } from "../../api/hooks/useCreateC2BPayment"
import { useGetSbpOutcomePaymentStatus } from "../../api/hooks/useGetSbpOutcomePaymentStatus"
import LoadingOverlay from "../../components/LoadingOverlay"

const C2BPushFlow = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const qrcInfo = useSelector((state: RootState) => state.c2bPayment.qrcInfo)
  const outcomePaymentId = useSelector((state: RootState) => state.c2bPayment.outcomePaymentId)
  const isPollingPaymentStatus = useSelector((state: RootState) => state.c2bPayment.isPollingPaymentStatus)
  const { isPending: isCreatingPayment } = useCreateC2BPayment()
  const { data: outcomePaymentData } = useGetSbpOutcomePaymentStatus(
    outcomePaymentId,
    isPollingPaymentStatus,
  )
  const isAwaitingPaymentStatus = isPollingPaymentStatus && Boolean(outcomePaymentId)
  const qrcIdParam = searchParams.get("qrcId")
  const isQrIndexRoute = location.pathname === `/${ROUTES_PATHS.qr}` || location.pathname === `/${ROUTES_PATHS.qr}/`

  // Check payment status
  useEffect(() => {
    if (!outcomePaymentData?.status || !qrcInfo?.qrcId) return

    switch (outcomePaymentData.status) {
      case OperationStatus.ACCEPTED:
        dispatch(stopPaymentStatusPolling())
        navigate(`/${ROUTES_PATHS.qr}/${qrcInfo?.qrcId}/success`)
        break
      case OperationStatus.ACCEPT_ERROR:
      case OperationStatus.REJECTED:
        dispatch(stopPaymentStatusPolling())
        navigate(`/${ROUTES_PATHS.qr}/error`)
        break
      default:
        break
    }
  }, [outcomePaymentData?.status, dispatch, navigate, qrcInfo?.qrcId])

  const handleClose = useCallback(() => {
    dispatch(resetSlice())
    navigate(`/${ROUTES_PATHS.main}`)
  }, [dispatch, navigate])

  const handleRetry = useCallback(() => {
    dispatch(resetSlice())
    navigate(`/${ROUTES_PATHS.qr}`)
  }, [dispatch, navigate])

  const handleQrDetected = useCallback((qrUrl: string) => {
    const params = parseQrPaymentUrl(qrUrl)

    if (!params?.qrcId) {
      navigate(`/${ROUTES_PATHS.qr}/error`)
      return
    }

    dispatch(setQrParams(params))
    navigate(`/${ROUTES_PATHS.qr}/${params.qrcId}`)
  },
    [dispatch, navigate],
  )

  useEffect(() => () => {
    dispatch(resetSlice())
  }, [dispatch])

  if (isCreatingPayment || isAwaitingPaymentStatus) {
    return <LoadingOverlay disableOpacity />
  }

  if (qrcIdParam && isQrIndexRoute) {
    return (
      <Navigate
        to={`/${ROUTES_PATHS.qr}/${qrcIdParam}`}
        replace
      />
    )
  }

  return (
    <Routes>
      <Route
        index
        element={
          <C2BPushScan
            onQrDetected={handleQrDetected}
            onClose={handleClose}
          />
        }
      />
      <Route
        path=":qrcId"
        element={
          <C2BPushPayment
            onRetry={handleRetry}
            onClose={handleClose}
          />
        }
      />
      <Route path=":qrcId/success" element={<C2BPushSuccess />} />
      <Route
        path="error"
        element={
          <C2BPushError
            onRetry={handleRetry}
            onClose={handleClose}
            title="Операция не выполнена"
            description="Проверьте QR-код и попробуйте снова"
          />
        }
      />
    </Routes>
  )
}

export default C2BPushFlow
